import type { Auth0VueClient } from '@auth0/auth0-vue';
import { inject, type App, type Plugin } from 'vue';
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  type NormalizedCacheObject,
  type ApolloClientOptions,
  type InMemoryCacheConfig,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { DefaultApolloClient, ApolloClients } from '@vue/apollo-composable';
import { createAuthLink, type AuthOptions } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

export { createAccountApi, useAccountApi };
export type { ApiConfig, MultiApiConfig };

type ApiConfig = AppSyncApiConfig | OtherApiConfig;
type MultiApiConfig = ApiConfig & { clientId: string };

type AppSyncApiConfig = {
  endpoint: string;
  authOptions?: AuthOptions;
  region?: string;
  apolloClientOptions?: Omit<
    ApolloClientOptions<NormalizedCacheObject>,
    'link' | 'cache'
  >;
  inMemoryCacheConfig?: InMemoryCacheConfig;
};
type OtherApiConfig = {
  client: ApolloClient<NormalizedCacheObject>;
};

// setting
const globalPropertyName = '$api';
const defaultRegion = 'ap-northeast-1';
const defaultApolloClientOptions: AppSyncApiConfig['apolloClientOptions'] = {
  defaultOptions: {
    watchQuery: {
      // client.query,useQueryは、queryでなくwatchQueryを参照
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    mutate: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      // TODO エラーページ表示処理
    });
  if (networkError) console.error(`[Network error]: ${networkError}`);
  // TODO エラーページ表示処理
});

/**
 * Apiを登録する
 * Auth0のOIDCで認証する場合はAuth0Pluginより後でuseすること
 *
 * @param api ApiConfig | MultiApiConfig[]
 * @returns Plugin
 */
function createAccountApi(api: ApiConfig): Plugin;
function createAccountApi(apis: MultiApiConfig[]): Plugin;
function createAccountApi(arg: ApiConfig | MultiApiConfig[]): Plugin {
  return {
    install: app => {
      if (!Array.isArray(arg)) {
        // Single Api
        const client = createApolloClient(app, arg);
        app.provide(DefaultApolloClient, client);
        app.config.globalProperties[globalPropertyName] = client;
      } else {
        // Multi Apis
        const clients: Record<string, ApolloClient<NormalizedCacheObject>> = {};
        arg.forEach(api => {
          clients[api.clientId] = createApolloClient(app, api);
        });
        app.provide(ApolloClients, clients);
        app.config.globalProperties[globalPropertyName] = clients;
      }
    },
  };
}

function useAccountApi(clientId?: string): ApolloClient<NormalizedCacheObject> {
  if (clientId) {
    const clients = inject(ApolloClients) as Record<
      string,
      ApolloClient<NormalizedCacheObject>
    >;
    return clients[clientId];
  }
  return inject(DefaultApolloClient) as ApolloClient<NormalizedCacheObject>;
}

function createApolloClient(app: App, apiConfig: ApiConfig) {
  // Other
  if ('client' in apiConfig) return apiConfig.client;
  // AppSync
  return createApolloClientForAppSync(
    apiConfig.endpoint,
    apiConfig.region || defaultRegion,
    apiConfig.authOptions || createOIDCAuthOptionsWithAuth0(app),
    { ...defaultApolloClientOptions, ...apiConfig.apolloClientOptions },
    apiConfig.inMemoryCacheConfig
  );
}

function createOIDCAuthOptionsWithAuth0(app: App): AuthOptions {
  const auth0 = app.config.globalProperties['$auth0'] as Auth0VueClient;
  return {
    type: 'OPENID_CONNECT',
    jwtToken: async () => {
      await auth0.getAccessTokenSilently();
      return auth0.idTokenClaims?.value?.__raw || '';
    },
  };
}

function createApolloClientForAppSync(
  url: string,
  region: string,
  auth: AuthOptions,
  apolloClientOptions?: AppSyncApiConfig['apolloClientOptions'],
  inMemoryCacheConfig?: InMemoryCacheConfig
): ApolloClient<NormalizedCacheObject> {
  // TODO CustomDomainの場合のWS挙動確認
  const httpLink = createAuthLink({ url, region, auth });
  const wsLink = createSubscriptionHandshakeLink({ url, region, auth });
  const concatLink = ApolloLink.from([
    errorLink,
    httpLink,
    wsLink,
  ] as unknown as ApolloLink[]);

  return new ApolloClient({
    link: concatLink,
    cache: new InMemoryCache(inMemoryCacheConfig),
    ...apolloClientOptions,
  });
}
